/**
 * Reading the theme and metadata records (decision 62: one record holds the theme and the book metadata) at
 * the boundary (Definition of Done, security: nothing unvalidated crosses into core types). A record comes
 * from the store, a pull or a bundle import, so every field is checked here, and anything else is refused
 * with a {@link RecordError} naming the field. Part 05 log, table V.
 */

/** A record that isn't a valid theme or metadata record. `field` names the offending field, or null for the whole. */
export class RecordError extends Error {
  override readonly name = 'RecordError';
  readonly field: string | null;

  constructor(field: string | null, problem: string) {
    super(field === null ? problem : `${field}: ${problem}`);
    this.field = field;
  }
}

/** A record's own data properties, read once each. */
export type Fields = ReadonlyMap<string, unknown>;

/**
 * The own properties of a plain object (V1–V5), for a record or for the object in a record's field at `path`
 * (V15: errors then name `path`, or `path.key`): its prototype is `Object.prototype` or null (what
 * `JSON.parse` and structured cloning make), every key is one of `keys` (V3, `__proto__` included), every
 * one of `keys` is present (V4; or named in `absent`, which gives its value, part 11h's MC1), and each is a data property, read once (V5: a getter could answer
 * differently on each read).
 */
export function readFields(value: unknown, keys: readonly string[], what: string, path: string | null = null, absent: ReadonlyMap<string, unknown> = new Map()): Fields {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new RecordError(path, `${what} must be an object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) throw new RecordError(path, `${what} must be a plain object`);
  const named = (key: string): string => (path === null ? key : `${path}.${key}`);
  const fields = new Map<string, unknown>();
  for (const own of Reflect.ownKeys(value)) {
    const key = keys.find((name) => name === own);
    if (key === undefined) throw new RecordError(path, `${what} has an unknown field "${String(own).slice(0, 64)}"`);
    // A proxy can list a key and then describe it as absent.
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !('value' in descriptor)) throw new RecordError(named(key), 'must be a data property');
    fields.set(key, descriptor.value);
  }
  for (const key of keys) {
    if (fields.has(key)) continue;
    // Part 11h, MC1: a key a later part added reads as its value from before, where the reader names one.
    if (!absent.has(key)) throw new RecordError(named(key), 'is missing');
    fields.set(key, absent.get(key));
  }
  return fields;
}

const isWholeNumber = (value: unknown): value is number => Number.isInteger(value);

/** A whole number from `min` to `max` on the grid `min + k × step` (V6–V9); -0 reads as 0. */
export function integerField(fields: Fields, key: string, min: number, max: number, step = 1): number {
  const value = fields.get(key);
  if (!isWholeNumber(value)) throw new RecordError(key, 'must be a whole number');
  if (value < min || value > max) throw new RecordError(key, `must be from ${min} to ${max}`);
  if ((value - min) % step !== 0) throw new RecordError(key, `must be a multiple of ${step} from ${min}`);
  return value + 0;
}

/** One of `values` (V10). */
export function choiceField<T extends string>(fields: Fields, key: string, values: readonly T[]): T {
  const value = fields.get(key);
  const found = values.find((choice) => choice === value);
  if (found === undefined) throw new RecordError(key, `must be one of ${values.join(', ')}`);
  return found;
}

/** `true` or `false` (V11). */
export function booleanField(fields: Fields, key: string): boolean {
  const value = fields.get(key);
  if (typeof value !== 'boolean') throw new RecordError(key, 'must be true or false');
  return value;
}

/**
 * `value` with it and every object inside it frozen, so a shared constant (a default, a preset, a profile)
 * can't be changed in place by whoever holds it, a UI store included (W3).
 */
export function frozen<T>(value: T): T {
  // Object.isFrozen is true for primitives and null, so only an unfrozen object gets past here.
  if (Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const descriptor of Object.values(Object.getOwnPropertyDescriptors(value))) frozen<unknown>(descriptor.value);
  return value;
}

/** The longest record text `parseRecordJson` reads; far above any valid record our writer makes (V12). */
export const MAX_RECORD_LENGTH = 256 * 1024;

/** JSON text to a value, refusing text longer than {@link MAX_RECORD_LENGTH} and text that isn't JSON (V12, V13). */
export function parseRecordJson(text: string, what: string): unknown {
  if (text.length > MAX_RECORD_LENGTH) throw new RecordError(null, `${what} is longer than ${MAX_RECORD_LENGTH}`);
  try {
    return JSON.parse(text);
  } catch {
    throw new RecordError(null, `${what} is not JSON`);
  }
}
